// Static report map. The reliable source is a direct GeoServer WMS GetMap PNG
// (same server the old interactive maps used); where the chatbot's composited
// map has been generated, that nicer framed version is swapped in. No pan or
// zoom, and the image captures into the exported PDF.

import { useEffect, useMemo, useRef, useState } from "react";

import { fetchReportMap } from "../api";
import { wmsMapUrl } from "../reportConfig";

// The composite isn't rendered on demand — a miss dispatches a Celery job
// (Cartopy: basemap tiles + GeoServer WMS fetch + matplotlib compositing) and
// this polls for it. A first-ever render routinely takes well past a few
// seconds, especially with several maps on one page queuing behind the same
// workers, so poll for a few minutes rather than give up after ~10s and be
// stuck showing the plain WMS image for the rest of the visit.
const COMPOSITE_TRIES = 24;
const COMPOSITE_POLL_MS = 8000;

// `req` and `bounds` are fresh objects on most renders (rebuilt while the AI
// narrative streams), so key every effect off the primitive values, not object
// identity — otherwise the image resets on every render.
function reqKeyOf(req) {
  if (!req) return "";
  return [req.indicator, req.year, req.month, req.dekad, req.timescale, req.forecastPeriod]
    .map((v) => v ?? "")
    .join("|");
}
function boundsKeyOf(bounds) {
  if (!bounds) return "";
  return `${bounds.minx},${bounds.miny},${bounds.maxx},${bounds.maxy}`;
}

/**
 * @param {object} props
 * @param {object|null} props.req Resolved map request from `buildMapRequest`.
 * @param {{minx, miny, maxx, maxy}|null} props.bounds Country bbox.
 * @param {string|number} props.countryId
 * @param {string} props.alt
 */
export default function ReportMap({ req, bounds, countryId, alt }) {
  const reqKey = reqKeyOf(req);
  const boundsKey = boundsKeyOf(bounds);
  // Everything that changes which image this component should be showing —
  // switching country changes bounds/countryId but not reqKey, and that alone
  // must still be treated as "a different map" (otherwise the previous
  // country's image just sits there with no loading indication while the new
  // one is fetched in the background).
  const identityKey = `${reqKey}|${boundsKey}|${countryId ?? ""}`;
  const unavailable = Boolean(req?.unavailable);
  const isForecast = req?.indicator === "longrange_forecast";

  const wmsUrl = useMemo(
    () => (req ? wmsMapUrl(req, bounds) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reqKey, boundsKey],
  );

  const [compositeUrl, setCompositeUrl] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const compositeTimer = useRef(null);
  const imgRef = useRef(null);

  const src = compositeUrl || wmsUrl;

  // Reset whenever the map's identity changes — including a country/bounds
  // change with the same indicator and period — so the stale image is hidden
  // behind the loading overlay instead of lingering until the new one lands.
  useEffect(() => {
    setStatus(unavailable ? "error" : "loading");
    setCompositeUrl(null);
  }, [identityKey, unavailable]);

  // Cached images can finish loading before React attaches onLoad — check the
  // element directly whenever the source changes.
  useEffect(() => {
    if (unavailable || !src) return;
    const img = imgRef.current;
    if (img && img.complete) {
      setStatus(img.naturalWidth > 0 ? "ready" : "error");
    }
  }, [src, unavailable]);

  // Opportunistically upgrade to the composited PNG once a worker has made one.
  useEffect(() => {
    clearTimeout(compositeTimer.current);
    // Forecast polygons have no per-season composite; use the WMS image only.
    if (!reqKey || unavailable || !countryId || isForecast) return undefined;

    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const res = await fetchReportMap({ ...req, countryId });
        if (!cancelled && res?.url) {
          setCompositeUrl(res.url);
          return;
        }
      } catch (err) {
        /* stay on the WMS image */
      }
      if (!cancelled && tries < COMPOSITE_TRIES) {
        compositeTimer.current = setTimeout(poll, COMPOSITE_POLL_MS);
      }
    };
    poll();
    return () => {
      cancelled = true;
      clearTimeout(compositeTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityKey, unavailable, isForecast]);

  const handleError = () => {
    if (compositeUrl) {
      setCompositeUrl(null); // composited image bad — drop back to the WMS one
    } else {
      setStatus("error");
    }
  };

  if (!req || unavailable || !src) {
    return (
      <div className="report-map">
        <div className="report-map__overlay">Map failed to load</div>
      </div>
    );
  }

  return (
    <div className="report-map">
      <img
        ref={imgRef}
        className="report-map__img"
        src={src}
        alt={alt}
        loading="lazy"
        onLoad={() => setStatus("ready")}
        onError={handleError}
      />
      {status === "loading" && (
        <div className="report-map__overlay">
          <span className="report-spinner" />
          <span>Loading map…</span>
        </div>
      )}
      {status === "error" && <div className="report-map__overlay">Map failed to load</div>}
    </div>
  );
}
